<script lang="ts" setup>
	import type { RuleObject } from 'ant-design-vue/es/form'
	import { useResourcesApi } from '~/services/apis/resources'

	const {
		type = 'RC',
		required = true,
		businessName
	} = defineProps<{
		type?: string
		required?: boolean
		businessName: string
		disabled?: boolean
	}>()
	const { validateCACNumber } = useResourcesApi()
	const validating = shallowRef(false)
	const verified = shallowRef(false)
	const name = shallowRef('')
	const rcRegex = /^(?:RC|BP|BN|IT)(?:\s*-\s*)?\d{4,}$/i
	const CACMIN = 4
	const emit = defineEmits<{
		done: [value: CACResponse]
	}>()
	const modelVal = defineModel<string | null | undefined>({
		required: true,
		default: ''
	})
	const validatingNumber = defineModel<boolean>('validatingNumber', {
		required: false,
		default: false
	})

	const handleRequest = async () => {
		if (validatingNumber.value) return {} as CACResponse
		// This is for staging
		if (import.meta.env.DEV) {
			verified.value = true
			const testData = { cac_verified: true, name: 'Test Business Name' } as CACResponse
			name.value = testData.name
			emit('done', testData)
			return testData
		}
		const response = await resolve(
			async () => validateCACNumber({ rcNumber: String(modelVal.value), name: businessName }),
			validatingNumber
		)
		if (response?.data?.cac_verified) {
			verified.value = true
			name.value = response.data.name
			emit('done', response.data)
			return response.data as CACResponse
		} else {
			useNotification().alert({
				status: 400,
				message: 'The provided name does not match your registered business name.'
			})
		}
		return {} as CACResponse
	}
	const handleCACValidation = async (rule: RuleObject, value: string) => {
		if (!required && !value) return Promise.resolve()
		if (!String(type)) {
			return Promise.reject('Please select a business type')
		} else if (value.length < CACMIN) {
			return Promise.reject(`CAC number must be at least ${CACMIN} characters long`)
		} else if (!rcRegex.test(value) || (!String(value).startsWith(type) && !import.meta.env.DEV)) {
			// NOTE: Using the DEV env to allow any CAC number to be accepted on staging
			return Promise.reject(`Please enter a valid CAC number (e.g: ${import.meta.env.DEV ? 'RC' : type}0000000)`)
		}
		return Promise.resolve()
	}
	defineExpose({
		handleValidation: handleRequest
	})
	watch(
		() => businessName,
		() => {
			name.value = ''
			verified.value = false
		}
	)
</script>

<template>
	<a-form-item
		label="CAC Registration number"
		name="cac_number"
		class="!mb-5"
		:rules="{ validator: handleCACValidation, trigger: 'blur' }"
	>
		<a-input
			v-model:value.trim="modelVal"
			:placeholder="`${type || 'RC'}0`"
			class="custom-input !bg-gray-50 !px-5"
			data-testid="cac_number"
			:disabled="validating || disabled"
		>
			<template #suffix>
				<tools-icon v-if="validating" icon="eclipse" dir="line" />
				<tools-icon v-else-if="verified" dir="fill" icon="check-2" />
			</template>
		</a-input>
		<transition name="animate" enter-active-class="animate__animated animate__fadeIn animate__faster">
			<div
				v-if="verified"
				class="mt-2 flex w-full items-center justify-between rounded-lg border border-[#9cd2c9] bg-[#f5fef9] px-[10px] py-[10px] sm:px-[20px]"
			>
				<div class="inline-flex gap-2.5">
					<tools-icon dir="fill" icon="briefcase" path-class="fill-gray-500" />
					<span class="text-sm font-medium text-[#667084]">Business Name</span>
				</div>
				<span class="justify-start text-right text-sm font-medium text-[#439687]">{{ businessName }}</span>
			</div>
		</transition>
	</a-form-item>
</template>
